import { Theme } from '@mui/material';

import { ContentsCardKindEnum, ContentsSourceEnum } from '../constants/createSendContents';
import { TCreateSendBucketItem, TMarketplaceBucketItem } from './bucket';
import { ICampaign } from './campaigns';
import { IGiftBudget, IMarketplaceSKUOption, IMarketplaceSupplier } from './marketplace';

export enum ContentsTabIdEnum {
  Curated = 'curated',
  Build = 'build',
}

export type TContentsTabId = ContentsTabIdEnum;

// Recipients and link share a tab selection; location has its own. Persisted with the slice.
export enum ContentsTabGroupEnum {
  RecipientsLink = 'recipients-link',
  Location = 'location',
}

export type TContentsTabGroup = ContentsTabGroupEnum;

export type TContentsFilter = 'your-inventory' | 'department' | 'warehouse' | 'backorder' | 'category';

export enum BannerRoleEnum {
  Alert = 'alert',
  Status = 'status',
}

export interface ISummaryHeaderCopy {
  title: string;
  subtitle: string;
}

export interface ICreateSendContentsState {
  activeTab: Partial<Record<TContentsTabGroup, TContentsTabId>>;
  kits: ICampaign[] | null;
  items: TCreateSendBucketItem[];
  boxId: string;
  giftBudget: IGiftBudget | null;
  isGiftBudgetPending: boolean;
  pygOptionOrder: string[];
}

export interface ISetContentsActiveTabPayload {
  group: TContentsTabGroup;
  tab: TContentsTabId;
}

// Stored back onto the bucket item so summary, review and payload read one shape. The sender
// never picks a variant in v1 — the recipient does at claim.
export interface ISetContentsItemSkuOptionsPayload {
  itemId: string;
  skuOptions: IMarketplaceSKUOption[];
  /** Omitted leaves the stored supplier. */
  supplier?: IMarketplaceSupplier | null;
}

export interface IChangeContentsItemValuePayload {
  id: string;
  name: string;
  value: unknown;
}

export type TContentsBlockReason =
  | 'gift-budget-incomplete'
  | 'empty-build'
  | 'empty-curated'
  | 'empty-location'
  | 'out-of-stock'
  | 'insufficient-stock'
  | 'out-of-stock-location'
  | 'insufficient-stock-location'
  | 'digital-location'
  | 'nothing-to-ship-location';

export interface ICreateSendUnsetMskuState {
  count: number;
  firstName: string;
  isSendEmpty: boolean;
  attempted: boolean;
  key: string | null;
}

export interface IContentsGate {
  valid: boolean;
  reason: TContentsBlockReason | null;
}

export interface IFilterOption {
  label: string;
  value: string;
}

export interface IContentsSummaryCopy {
  emptyTitle: string;
  emptyBody: string;
}

export interface IContentsTabConfig {
  id: TContentsTabId;
  label: string;
  cardKind: ContentsCardKindEnum;
  searchPlaceholder: string;
  filters: TContentsFilter[];
  summary: IContentsSummaryCopy;
}

export interface IContentsConfig {
  tabs: IContentsTabConfig[];
  clearCartOnTabSwitch: boolean;
  tabGroup: TContentsTabGroup;
}

/** A colour usable in `sx`: a theme path/literal, or a callback resolved against the theme. */
export type TToneColor = string | ((theme: Theme) => string);

/** Discriminated on `ContentsSourceEnum`, the same vocabulary the source lock and tabs use. */
export type TPygOption =
  | { kind: ContentsSourceEnum.IeCatalog; id: string; kit: ICampaign }
  | { kind: ContentsSourceEnum.Marketplace; id: string; item: TMarketplaceBucketItem };
